import { useEffect, useState } from "react";

let deferred = null;
const subs = new Set();
const emit = () => subs.forEach((f) => f());
window.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); deferred = e; emit(); });
window.addEventListener("appinstalled", () => { deferred = null; emit(); });

export const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
export const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || navigator.standalone === true;

/** Install availability: "prompt" (Android/desktop), "ios" (instruction sheet), or null (installed / not offered). */
export const useInstall = () => {
  const [, tick] = useState(0);
  useEffect(() => { const f = () => tick((n) => n + 1); subs.add(f); return () => subs.delete(f); }, []);
  if (isStandalone()) return null;
  return deferred ? "prompt" : isIOS() ? "ios" : null;
};

export async function promptInstall() {
  if (!deferred) return false;
  deferred.prompt();
  const r = await deferred.userChoice;
  deferred = null;
  emit();
  return r.outcome === "accepted";
}

const API = process.env.REACT_APP_BACKEND_URL;
const setLink = (rel, href, attrs = {}) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) { el = document.createElement("link"); el.rel = rel; document.head.appendChild(el); }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  el.href = href;
};
const setMeta = (name, content) => {
  let el = document.head.querySelector(`meta[name="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.name = name; document.head.appendChild(el); }
  el.content = content;
};

let ICON_V = "?v=brain1";
const icon = (n) => `${API}/api/brand/app-icon/${n}${ICON_V}`;
let lastBrand = [null, null];

/** Loads the global app-icon version (cache-busting) and registers the service worker for it (a new version re-registers it). */
export async function initAppIcon() {
  try { const r = await fetch(`${API}/api/brand/app-icon-meta`); ICON_V = `?v=${(await r.json()).version}`; } catch (_) { /* keep default */ }
  applyBrand(...lastBrand);
  if ("serviceWorker" in navigator) navigator.serviceWorker.register(swUrl()).catch(() => {});
}
export const swUrl = () => `/sw.js${ICON_V}`;

/** Point the manifest at a company (or the neutral platform when slug is empty); every app icon/favicon is the brain icon. */
export function applyBrand(slug, name) {
  setLink("manifest", `${API}/api/brand/${slug || "_platform"}/manifest.webmanifest${ICON_V}`);
  lastBrand = [slug, name];
  setLink("icon", icon("favicon-32.png"), { type: "image/png", sizes: "32x32" });
  setLink("shortcut icon", icon("favicon.ico"));
  setLink("apple-touch-icon", icon("apple-touch-icon-180.png"), { sizes: "180x180" });
  setMeta("theme-color", "#0B1B33");
  setMeta("apple-mobile-web-app-title", name || "THE BRAIN");
}

const PAGES = [["/dashboard", "Dashboard"], ["/sales", "Sales Analysis"], ["/outstanding", "Outstanding"], ["/expenses", "Expenses"], ["/cogs", "COGS"],
  ["/reports", "Reports"], ["/data/uploads", "Uploads"], ["/data/history", "Upload History"], ["/data/mapping", "Expense Mapping"], ["/admin/data", "Data Management"],
  ["/admin/companies", "Companies"], ["/admin/proposals", "Proposals"], ["/admin/visibility", "Public view defaults"], ["/company/sharing", "Viewer links"],
  ["/work", "Tasks & Approvals"], ["/tasks", "Tasks"], ["/approvals", "Approvals"], ["/documents", "Documents"], ["/notifications", "Notifications"],
  ["/profile", "Profile"], ["/settings", "Settings"], ["/share", ""]];
export const pageLabel = (path) => (PAGES.find(([p]) => path === p || path.startsWith(`${p}/`)) || [, ""])[1];

/** Tab title: the company name (optionally "· page"); "THE BRAIN" without a company. */
export const setTitle = (company, page) => { document.title = company ? (page ? `${company} · ${page}` : company) : (page ? `${page} · THE BRAIN` : "THE BRAIN"); };

export const useMedia = (q) => {
  const [m, setM] = useState(() => window.matchMedia(q).matches);
  useEffect(() => {
    const mq = window.matchMedia(q);
    const f = () => setM(mq.matches);
    mq.addEventListener("change", f);
    return () => mq.removeEventListener("change", f);
  }, [q]);
  return m;
};
export const useIsMobile = () => useMedia("(max-width: 767px)");
